import React, { useEffect, useRef, useState } from "react";
import { Button as UIButton } from "theme-ui";
import { Button } from "@shared-components/Button/Button";

import {
  MainStyles,
  ContainerStyles,
  StorySectionsStyles,
  ThumbnailContainerStyles,
  ThumbnailsStyles,
  ThumbnailButtonStyles,
  ThumbnailButtonSelectedStyles,
  SectionTitleStyles,
  HeadingStyles,
  LargeContentTextStyles,
  TwoColumnsContentStyles,
  OverflowBox,
  MobileLandscapeStyles,
  MobileLandscapeContentStyles,
  ExploreButtonWrapperStyles,
} from "./styles";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, {
  EffectFade,
  Navigation,
  Pagination,
  Controller,
} from "swiper";

import "swiper/swiper-bundle.css";
import { motion } from "framer-motion";
import { Box } from "@shared-components/Box/Box";
import { Heading } from "@shared-components/Heading/Heading";
import { Text } from "@shared-components/Text/Text";
import {
  StorySlide,
  StorySliderModule,
  StorySliderSection,
} from "../../carlsberg-headless/types/modules/StorySlider";
import ParseHtmlToReact from "@shared-components/ParseHtmlToReact/ParseHtmlToReact";
import { ImageField } from "src/carlsberg-headless/types/fields/Image";
import { UmbracoImage } from "@shared-components/Image/UmbracoImage/UmbracoImage";
import { Container } from "@shared-components/Container/Container";
import { Icon } from "@shared-components/Icon/Icon";
import MediaSliderArrow from "@svg-icons/media-slider-arrow.component.svg";
import MediaSliderEllipse from "@svg-icons/media-slider-ellipse.component.svg";
import MobileLandscapeIcon from "@svg-icons/phone-turn.component.svg";
import {
  ellipseStyles,
  leftArrowSpanStyles,
  leftArrowStyles,
  rightArrowSpanStyles,
  rightArrowStyles,
} from "@modules/MediaSlider/styles";
import {
  ThumbnailButtonSelectedAnimation,
  ThumbnailButtonSelectedMobileAnimation,
} from "./animations";
import { useWindowSize } from "../../shared/hooks/useWindowSize/useWindowSize";
import { UmbracoBackgroundImage } from "@shared-components/Image/UmbracoBackgroundImage/UmbracoBackgroundImage";
import { BackgroundVideo } from "@shared-components/BackgroundVideo/BackgroundVideo";
import { Nullable } from "src/shared/types/util";
import { FlexRow, FlexCell } from "@shared-components/FlexGrid/FlexGrid";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../context/site/useSiteContext";
import { AspectRatio } from "@shared-components/AspectRatio/AspectRatio";
import { THEMES_MAP } from "../../carlsberg-headless/themes-map";

SwiperCore.use([Controller, Navigation, Pagination, EffectFade]);

export interface StorySliderProps extends StorySliderModule {
  clickToContinueButtonText?: string;
  rotateText?: string;
}

export interface MobileLandscapeContentProps {
  rotateText?: string;
}

const MobileLandscapeContent: React.FC<MobileLandscapeContentProps> = ({
  rotateText,
}) => (
  <Box sx={MobileLandscapeContentStyles}>
    <Icon svg={MobileLandscapeIcon} />
    {rotateText && (
      <Heading as="p" variant="h6" sx={{ mt: 6 }}>
        {rotateText}
      </Heading>
    )}
  </Box>
);

export interface ThumbnailProps {
  thumbnail: ImageField;
  sectionTitle: Nullable<string>;
}
export interface ThumbnailsProps {
  thumbnails: ThumbnailProps[];
  onGoToSlide: (index: number) => void;
  currentSectionIndex?: number;
  overlayAnimated: boolean;
}

const getAnimationState = (
  overlayAnimated: boolean,
  activeSection: boolean
) => {
  if (!overlayAnimated) return "default";
  return activeSection ? "selected" : "noneSelected";
};

const Thumbnails: React.FC<ThumbnailsProps> = ({
  thumbnails,
  onGoToSlide,
  currentSectionIndex,
  overlayAnimated,
}) => {
  const windowSize = useWindowSize();
  const isMobile = windowSize.width && windowSize.width < 768;

  if (
    (!windowSize.width && !windowSize.height) ||
    (windowSize.width &&
      windowSize.height &&
      windowSize.width > windowSize.height &&
      isMobile)
  )
    return null;

  return (
    <Box sx={ThumbnailsStyles}>
      <Swiper slidesPerView="auto" centerInsufficientSlides={true}>
        {thumbnails?.map((item: ThumbnailProps, index: number) => {
          const activeSection = currentSectionIndex === index + 1;
          const thumbKey = `thumb_${item.sectionTitle ?? ""}_${index}`;
          return (
            <SwiperSlide key={thumbKey}>
              <motion.div
                initial="default"
                animate={getAnimationState(overlayAnimated, activeSection)}
                variants={
                  isMobile
                    ? ThumbnailButtonSelectedMobileAnimation
                    : ThumbnailButtonSelectedAnimation
                }
                style={{ border: 0, background: "transparent", padding: 0 }}
              >
                <UIButton
                  onClick={() => onGoToSlide(index + 1)}
                  sx={{
                    ...ThumbnailButtonStyles,
                    ...(activeSection && ThumbnailButtonSelectedStyles),
                  }}
                >
                  <AspectRatio ratio={["1:1"]}>
                    {item.thumbnail && (
                      <UmbracoImage
                        isCover
                        image={item.thumbnail}
                        width={[70, 70, 70]}
                        height={[70, 70, 70]}
                        lazyLoadOffset={0}
                      />
                    )}
                  </AspectRatio>
                </UIButton>
                {item.sectionTitle && (
                  <Text
                    sx={{
                      ...SectionTitleStyles,
                      ...{ opacity: activeSection ? [0, 0, 1] : 0 },
                    }}
                  >
                    {item.sectionTitle}
                  </Text>
                )}
              </motion.div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Box>
  );
};

interface ThumbnailsContentProps extends StorySliderProps {
  overlayAnimated: boolean;
  thumbnails: ThumbnailProps[];
  gotoSection: (index: number, slideIndex?: number) => void;
  currentSectionIndex: number;
}

const ThumbnailsContent: React.FC<ThumbnailsContentProps> = ({
  overlayAnimated,
  thumbnails,
  gotoSection,
  currentSectionIndex,
}) => (
  <Container
    sx={{
      ...ThumbnailContainerStyles,
    }}
  >
    {thumbnails && (
      <Thumbnails
        thumbnails={thumbnails}
        onGoToSlide={gotoSection}
        currentSectionIndex={currentSectionIndex}
        overlayAnimated={overlayAnimated}
      />
    )}
  </Container>
);

interface FirstSectionContentProps
  extends Omit<StorySliderProps, "clickToContinueButtonText"> {
  overlayAnimated: boolean;
  gotoSection: (index: number, slideIndex?: number) => void;
  clickToContinueButtonText?: string;
}
const FirstSectionContent: React.FC<FirstSectionContentProps> = ({
  gotoSection,
  clickToContinueButtonText,
  ...props
}) => {
  return (
    <Container
      sx={{
        ...ContainerStyles,
        ...(props.themes?.includes(
          THEMES_MAP.story_module.story_module_transparent_background_overlay
        ) && {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        }),
      }}
    >
      {props.headline && (
        <Box sx={OverflowBox}>
          <Heading as="h1" sx={HeadingStyles}>
            {props.headline}
          </Heading>
          {props.body && (
            <ParseHtmlToReact html={props.body} sx={LargeContentTextStyles} />
          )}
        </Box>
      )}
      <Box sx={ExploreButtonWrapperStyles}>
        <Button
          sx={{
            textTransform: "uppercase",
          }}
          onClick={() => gotoSection(1, 0)}
          variant="whiteOutline"
          outline
        >
          {clickToContinueButtonText ?? "Click to explore"}
        </Button>
      </Box>
    </Container>
  );
};

const SliderContent: React.FC<StorySlide> = (slide) => {
  return (
    <Container
      sx={{
        ...ContainerStyles,
        ...(slide.themes?.includes(
          THEMES_MAP.story_module.story_module_transparent_background_overlay
        ) && {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        }),
      }}
    >
      <Box sx={OverflowBox}>
        {slide.headline && (
          <Heading as="h2" sx={HeadingStyles}>
            {slide.headline}
          </Heading>
        )}
        {slide.content && slide.content2 && (
          <FlexRow spacing={[2, 6, 8]}>
            {slide.content && (
              <FlexCell col={[12, 6]}>
                <ParseHtmlToReact
                  html={slide.content}
                  sx={TwoColumnsContentStyles}
                />
              </FlexCell>
            )}
            {slide.content2 && (
              <FlexCell col={[12, 6]}>
                <ParseHtmlToReact
                  html={slide.content2}
                  sx={TwoColumnsContentStyles}
                />
              </FlexCell>
            )}
          </FlexRow>
        )}
        {slide.content && !slide.content2 && (
          <ParseHtmlToReact html={slide.content} sx={LargeContentTextStyles} />
        )}
      </Box>
    </Container>
  );
};

const PureStorySlider: React.FC<StorySliderProps> = ({
  rotateText,
  ...props
}) => {
  const thumbnails = [] as ThumbnailProps[];
  if (props.storySections?.length !== 0) {
    for (const section of props.storySections ?? []) {
      if (section.storySlides?.length !== 0) {
        thumbnails.push({
          thumbnail: section.thumbnail as ImageField,
          sectionTitle: section.sectionTitle,
        });
      }
    }
  }

  const swiperSectionInstance = useRef<SwiperCore>();

  const swiperSlideInstances = useRef<SwiperCore[]>([]);
  const swiperPrevButton = useRef<HTMLButtonElement[]>([]);
  const swiperNextButton = useRef<HTMLButtonElement[]>([]);

  const [isOverlayAnimated, setIsOverlayAnimated] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const initSwiper = (swiper: SwiperCore) => {
    swiperSectionInstance.current = swiper;
  };

  const onChangeSectionSwiper = (swiper: SwiperCore) => {
    if (swiper.activeIndex > 0) {
      setIsOverlayAnimated(true);
    } else {
      setIsOverlayAnimated(false);
    }
    setCurrentSectionIndex(swiper.activeIndex);
  };

  const gotoSection = (index: number, slideIndex?: number): void => {
    const swiperSlideIndex = index > 0 ? index - 1 : 0;

    if (swiperSlideInstances.current[swiperSlideIndex]) {
      swiperSlideInstances.current[swiperSlideIndex].slideTo(slideIndex ?? 0);
    }

    swiperSectionInstance.current?.slideTo(index);
  };

  const gotoPrev = (): void => {
    if (!swiperSectionInstance.current) return;

    const activeSectionIndex = swiperSectionInstance.current?.activeIndex;
    const realCurrentIndex =
      activeSectionIndex === 0 ? activeSectionIndex : activeSectionIndex - 1;

    if (swiperSlideInstances.current[realCurrentIndex].isBeginning) {
      // Goto next section first slide
      if (swiperSectionInstance.current?.activeIndex === 1) {
        gotoSection(0, 0);
      } else {
        const prevSectionLastSlide =
          swiperSlideInstances.current[realCurrentIndex - 1].slides.length || 0;
        gotoSection(activeSectionIndex - 1, prevSectionLastSlide - 1);
      }
    } else {
      // Goto next slide in current section
      swiperSlideInstances.current[realCurrentIndex].slidePrev();
    }
  };

  const gotoNext = (): void => {
    if (!swiperSectionInstance.current) return;

    const activeSectionIndex = swiperSectionInstance.current?.activeIndex;
    const realCurrentIndex =
      activeSectionIndex === 0 ? activeSectionIndex : activeSectionIndex - 1;

    if (swiperSlideInstances.current[realCurrentIndex].isEnd) {
      // Goto next section first slide
      if (swiperSectionInstance.current?.isEnd) {
        gotoSection(0, 0);
      } else {
        gotoSection(activeSectionIndex + 1, 0);
      }
    } else {
      // Goto next slide in current section
      swiperSlideInstances.current[realCurrentIndex].slideNext();
    }
  };

  useEffect(() => {
    const isBrowser = typeof window !== "undefined";

    const keyUp = (e: KeyboardEvent) => {
      e.stopImmediatePropagation();
      if (e.key === "ArrowLeft" || e.keyCode === 37) {
        if (swiperSectionInstance.current?.activeIndex === 0) {
          return;
        }
        gotoPrev();
      }
      if (e.key === "ArrowRight" || e.keyCode === 39) {
        if (swiperSectionInstance.current?.activeIndex === 0) {
          gotoSection(1, 0);
        } else {
          gotoNext();
        }
      }
    };

    if (isBrowser) {
      document.addEventListener("keyup", keyUp);
    }

    return () => {
      document.removeEventListener("keyup", keyUp);
    };
  }, []);

  const swiperProps = {
    scrollbar: { draggable: false },
    simulateTouch: false,
    shortSwipes: true,
    draggable: false,
  };

  return (
    <Box
      sx={{
        ...MainStyles,
      }}
    >
      <Box sx={MobileLandscapeStyles}>
        {props.video?.fullUrl && (
          <BackgroundVideo src={props.video.fullUrl}>
            <MobileLandscapeContent rotateText={rotateText} />
          </BackgroundVideo>
        )}
        {!props.video?.fullUrl && props.image?.fullUrl && (
          <UmbracoBackgroundImage
            image={props.image}
            height={[650, 1050]}
            lazyLoadOffset={0}
          >
            <MobileLandscapeContent rotateText={rotateText} />
          </UmbracoBackgroundImage>
        )}
      </Box>

      <Box sx={StorySectionsStyles}>
        <ThumbnailsContent
          overlayAnimated={isOverlayAnimated}
          thumbnails={thumbnails}
          gotoSection={gotoSection}
          currentSectionIndex={currentSectionIndex}
          {...props}
        />

        <Swiper
          onSwiper={(swiper) => initSwiper(swiper)}
          effect="fade"
          {...swiperProps}
          onSlideChange={(swiper) => onChangeSectionSwiper(swiper)}
        >
          <SwiperSlide>
            {props.video?.fullUrl && (
              <BackgroundVideo src={props.video.fullUrl}>
                <FirstSectionContent
                  overlayAnimated={isOverlayAnimated}
                  gotoSection={gotoSection}
                  clickToContinueButtonText={props.clickToContinueButtonText}
                  {...props}
                />
              </BackgroundVideo>
            )}
            {!props.video?.fullUrl && props.image?.fullUrl && (
              <UmbracoBackgroundImage
                image={props.image}
                height={[650, 1050]}
                lazyLoadOffset={0}
              >
                <FirstSectionContent
                  overlayAnimated={isOverlayAnimated}
                  gotoSection={gotoSection}
                  {...props}
                />
              </UmbracoBackgroundImage>
            )}
          </SwiperSlide>

          {props.storySections?.map(
            (section: StorySliderSection, index: number) => {
              const storiesLength = section.storySlides?.length;

              if (storiesLength) {
                const initSwiperChild = (swiper: SwiperCore) => {
                  swiperSlideInstances.current[index] = swiper;
                  swiperSlideInstances.current[index].navigation.prevEl =
                    swiperPrevButton[index];
                  swiperSlideInstances.current[index].navigation.nextEl =
                    swiperNextButton[index];
                };

                return (
                  <SwiperSlide
                    key={`${props.cbKey}_${index}`}
                    virtualIndex={index}
                  >
                    <Swiper
                      nested
                      effect="fade"
                      pagination={{ clickable: true }}
                      onSwiper={(swiper) => initSwiperChild(swiper)}
                      {...swiperProps}
                      observer={true}
                      observeParents={true}
                    >
                      {section.storySlides?.map(
                        (slide: StorySlide, sliderindex: number) => (
                          <SwiperSlide
                            key={`${props.cbKey}_${index}_${sliderindex}`}
                            virtualIndex={sliderindex}
                          >
                            {slide.video?.fullUrl && (
                              <BackgroundVideo src={slide.video.fullUrl}>
                                <SliderContent {...slide} />
                              </BackgroundVideo>
                            )}
                            {!slide.video?.fullUrl && slide.image?.fullUrl && (
                              <UmbracoBackgroundImage
                                image={slide.image}
                                height={[650, 1050]}
                              >
                                <SliderContent {...slide} />
                              </UmbracoBackgroundImage>
                            )}
                          </SwiperSlide>
                        )
                      )}
                    </Swiper>
                    <button
                      className="swiper-button-prev"
                      ref={swiperPrevButton[index]?.current}
                      onClick={() => gotoPrev()}
                    >
                      <Box sx={leftArrowSpanStyles}>
                        <Icon sx={leftArrowStyles} svg={MediaSliderArrow} />
                      </Box>
                      <Icon sx={ellipseStyles} svg={MediaSliderEllipse} />
                    </button>
                    <button
                      className="swiper-button-next"
                      ref={swiperNextButton[index]?.current}
                      onClick={() => gotoNext()}
                    >
                      <Box sx={rightArrowSpanStyles}>
                        <Icon sx={rightArrowStyles} svg={MediaSliderArrow} />
                      </Box>
                      <Icon sx={ellipseStyles} svg={MediaSliderEllipse} />
                    </button>
                  </SwiperSlide>
                );
              } else {
                return null;
              }
            }
          )}
        </Swiper>
      </Box>
    </Box>
  );
};

const StorySlider: React.FC<StorySliderProps> = (props) => {
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();
  return (
    <PureStorySlider
      clickToContinueButtonText={get(
        DICTIONARY_MAP.storyslider.storyslider_click_to_explore_text,
        currentCulture
      )}
      rotateText={get(
        DICTIONARY_MAP.storyslider.storyslider_rotate_text,
        currentCulture
      )}
      {...props}
    />
  );
};

StorySlider.defaultProps = {};

export { StorySlider, PureStorySlider };
